import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import maintenanceService from "../api/maintenance.service";

import {
  AssignTechnicianPayload,
  CloseTicketPayload,
  MaintenanceAnalytics,
  MaintenanceFilters,
  Pagination,
  ServiceTicket,
  ServiceTicketPayload,
  TicketStatus,
} from "../types";

const defaultPagination: Pagination = {
  page: 1,
  limit: 10,
  total: 0,
  totalPages: 0,
};

export const useMaintenance = () => {
  const [searchParams] = useSearchParams();
  const requestedType = searchParams.get("serviceType");
  const linkedServiceType = requestedType === "ON_ROAD" || requestedType === "IN_HOUSE" || requestedType === "PREVENTIVE"
    ? requestedType : undefined;
  const [serviceTickets, setServiceTickets] =
    useState<ServiceTicket[]>([]);

  const [analytics, setAnalytics] =
    useState<MaintenanceAnalytics | null>(null);

  const [loading, setLoading] =
    useState(false);

  const [error, setError] =
    useState<string | null>(null);

  const [pagination, setPagination] =
    useState<Pagination>(
      defaultPagination
    );

  const [filters, setFilters] =
    useState<MaintenanceFilters>({
      page: 1,
      limit: 10,
      serviceType: linkedServiceType,
    });
  useEffect(() => {
    if (!linkedServiceType) return;
    setFilters((current) => current.serviceType === linkedServiceType ? current : {
      ...current, serviceType: linkedServiceType, page: 1, search: "",
    });
  }, [linkedServiceType]);
  const refreshInProgress = useRef(false);
  const requestVersion = useRef(0);

  const fetchServiceTickets =
    useCallback(async (silent = false) => {
      const version = ++requestVersion.current;
      try {
        if (!silent) {
          setLoading(true);
        }

        const response =
          await maintenanceService.getServiceTickets(
            filters
          );
        if (version !== requestVersion.current) return;

        setServiceTickets(
          response.items
        );

        setPagination(
          response.pagination
        );

        setError(null);
      } catch (err) {
        if (version !== requestVersion.current) return;
        setError(
          "Failed to load service tickets."
        );
      } finally {
        if (version === requestVersion.current) {
          setLoading(false);
        }
      }
    }, [filters]);

  const fetchAnalytics =
    useCallback(async () => {
      try {
        const data =
          await maintenanceService.getMaintenanceAnalytics();

        setAnalytics(data);
      } catch {
        console.error(
          "Failed to load analytics."
        );
      }
    }, []);

  useEffect(() => {
    fetchServiceTickets();
  }, [fetchServiceTickets]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  useEffect(() => {
    const refreshVisibleMaintenanceData = async () => {
      if (document.visibilityState !== "visible" || refreshInProgress.current) {
        return;
      }

      refreshInProgress.current = true;
      try {
        await Promise.all([fetchServiceTickets(true), fetchAnalytics()]);
      } finally {
        refreshInProgress.current = false;
      }
    };

    const interval = window.setInterval(refreshVisibleMaintenanceData, 10_000);
    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") {
        void refreshVisibleMaintenanceData();
      }
    };

    document.addEventListener("visibilitychange", refreshWhenVisible);

    return () => {
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
    };
  }, [fetchAnalytics, fetchServiceTickets]);

  const createServiceTicket =
    async (
      payload: ServiceTicketPayload
    ) => {
      await maintenanceService.createServiceTicket(
        payload
      );

      await fetchServiceTickets();
    };

  const updateServiceTicket =
    async (
      id: string,
      payload: ServiceTicketPayload
    ) => {
      await maintenanceService.updateServiceTicket(
        id,
        payload
      );

      await fetchServiceTickets();
    };

  const deleteServiceTicket =
    async (id: string) => {
      await maintenanceService.deleteServiceTicket(
        id
      );

      await fetchServiceTickets();
    };
      const assignTechnician =
    async (
      id: string,
      payload: AssignTechnicianPayload
    ) => {
      await maintenanceService.assignTechnician(
        id,
        payload
      );

      await fetchServiceTickets();
    };

  const updateTicketStatus =
    async (
      id: string,
      status: TicketStatus,
      payload?: Pick<CloseTicketPayload, "actualCost">
    ) => {
      await maintenanceService.updateTicketStatus(
        id,
        status,
        payload
      );

      await fetchServiceTickets();
    };

  const closeTicket =
    async (
      id: string,
      payload?: CloseTicketPayload
    ) => {
      await maintenanceService.closeTicket(id, payload);

      await fetchServiceTickets();

      await fetchAnalytics();
    };

  const updateFilters = (
    newFilters: Partial<MaintenanceFilters>
  ) => {
    setFilters((prev) => ({
      ...prev,
      ...newFilters,
    }));
  };

  const refresh = async () => {
    await Promise.all([
      fetchServiceTickets(),
      fetchAnalytics(),
    ]);
  };

  return {
    serviceTickets,
    analytics,

    loading,
    error,

    pagination,
    filters,

    fetchServiceTickets,
    fetchAnalytics,
    refresh,

    createServiceTicket,
    updateServiceTicket,
    deleteServiceTicket,

    assignTechnician,
    updateTicketStatus,
    closeTicket,

    updateFilters,
  };
};
